/**
 * Service worker teardown — runs on every platform.
 *
 * The PWA is retired, so nothing registers a worker any more. Every install that ran an
 * earlier build still has one on disk though, plus its caches, and neither is removed by
 * deleting code. This clears both at startup.
 *
 * On NATIVE this is the whole fix. Bundled builds are served from `https://localhost`, where
 * service workers are fully supported, so the old unguarded `registerSW()` really did register
 * one and precache the app shell inside the Capacitor WebView (observed on a release APK: a
 * `workbox-precache-v2-https://localhost/` cache). That is a second caching layer underneath
 * Capgo, which updates the app by swapping `dist/` on disk — a precache serving the previous
 * shell out of Cache Storage is how an OTA comes to look like it applied and then didn't.
 *
 * On WEB this is only half of it, and the lesser half: it cleans up a client that has already
 * loaded this build. A client still pinned to a stale cached shell never runs this code at
 * all, and the only thing that reaches it is the tombstone worker served at `/sw-push.js`,
 * which the browser re-fetches on its own update check. The two are complementary — keep both.
 */

/**
 * Cache namespaces the retired worker owned: workbox's own precache/runtime caches, plus the
 * ones sw-push.js named itself. Scoped by prefix rather than clearing everything, so this
 * cannot eat a cache some future feature legitimately owns.
 */
const SW_CACHE_PREFIXES = ['workbox-', 'go-trade-loop-'];

export async function teardownServiceWorker(): Promise<void> {
  try {
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      if (registrations.length > 0) {
        await Promise.all(registrations.map((registration) => registration.unregister()));
        console.log(`[SW] Unregistered ${registrations.length} service worker(s)`);
      }
    }

    if ('caches' in window) {
      const names = await caches.keys();
      const stale = names.filter((name) => SW_CACHE_PREFIXES.some((prefix) => name.startsWith(prefix)));
      if (stale.length > 0) {
        await Promise.all(stale.map((name) => caches.delete(name)));
        console.log('[SW] Deleted retired service worker caches:', stale);
      }
    }
  } catch (error) {
    // Never let cleanup break boot: a worker we failed to remove is dormant, an app that
    // failed to start is not.
    console.error('[SW] Service worker teardown failed:', error);
  }
}
